import type { CSSProperties } from "react";
import http from "./http";

export type AdminTextFontSizePreset = "sm" | "md" | "lg";
export type AdminTextFontWeight = "normal" | "bold";
export type AdminTextFontStyle = "normal" | "italic";

export type AdminTextStylePreset = {
	fontSizePreset: AdminTextFontSizePreset;
	fontWeight: AdminTextFontWeight;
	fontStyle: AdminTextFontStyle;
};

export type AdminTextPaletteEntry = {
	token: string;
	label: string;
	value: string;
};

export type AdminTextEntry = {
	key: string;
	screen: string;
	group: string;
	defaultText: string;
	currentText: string;
	stylePreset: AdminTextStylePreset;
	colorToken: string;
	status: "draft" | "published";
	updatedAt?: string | null;
};

export type AdminTextRegistry = {
	version: number;
	palette: AdminTextPaletteEntry[];
	items: AdminTextEntry[];
	updatedAt?: string | null;
	publishedAt?: string | null;
};

const DEFAULT_PALETTE: AdminTextPaletteEntry[] = [
	{ token: "default", label: "Default", value: "#0f172a" },
	{ token: "white", label: "White", value: "#ffffff" },
	{ token: "primary", label: "Primary", value: "#2563eb" },
	{ token: "secondary", label: "Secondary", value: "#0f766e" },
	{ token: "muted", label: "Muted", value: "#64748b" },
	{ token: "success", label: "Success", value: "#15803d" },
	{ token: "danger", label: "Danger", value: "#dc2626" },
	{ token: "warning", label: "Warning", value: "#b45309" },
];

const DEFAULT_STYLE_PRESET: AdminTextStylePreset = {
	fontSizePreset: "md",
	fontWeight: "normal",
	fontStyle: "normal",
};

const EMPTY_REGISTRY: AdminTextRegistry = {
	version: 1,
	palette: DEFAULT_PALETTE,
	items: [],
	updatedAt: null,
	publishedAt: null,
};

let cachedRegistry: AdminTextRegistry | null = null;
let pendingRegistryPromise: Promise<AdminTextRegistry> | null = null;

function normalizeStylePreset(value: Partial<AdminTextStylePreset> | undefined): AdminTextStylePreset {
	return {
		fontSizePreset: value?.fontSizePreset === "sm" || value?.fontSizePreset === "lg" ? value.fontSizePreset : "md",
		fontWeight: value?.fontWeight === "bold" ? "bold" : "normal",
		fontStyle: value?.fontStyle === "italic" ? "italic" : "normal",
	};
}

function normalizeEntry(value: Partial<AdminTextEntry> | undefined): AdminTextEntry | null {
	const key = String(value?.key || "").trim();
	if (!key) return null;

	return {
		key,
		screen: String(value?.screen || "general").trim(),
		group: String(value?.group || "general").trim(),
		defaultText: String(value?.defaultText || "").trim(),
		currentText: typeof value?.currentText === "string" ? value.currentText : "",
		stylePreset: normalizeStylePreset(value?.stylePreset),
		colorToken: String(value?.colorToken || "default").trim() || "default",
		status: value?.status === "draft" ? "draft" : "published",
		updatedAt: typeof value?.updatedAt === "string" ? value.updatedAt : null,
	};
}

export function normalizeAdminTextRegistry(value: Partial<AdminTextRegistry> | undefined): AdminTextRegistry {
	const palette = Array.isArray(value?.palette) && value?.palette.length > 0 ? value.palette : DEFAULT_PALETTE;
	const items = Array.isArray(value?.items)
		? (value.items.map(normalizeEntry).filter(Boolean) as AdminTextEntry[])
		: [];

	return {
		version: Number(value?.version || 1) || 1,
		palette,
		items,
		updatedAt: typeof value?.updatedAt === "string" ? value.updatedAt : null,
		publishedAt: typeof value?.publishedAt === "string" ? value.publishedAt : null,
	};
}

export async function getPublishedAdminTextRegistry(forceRefresh = false): Promise<AdminTextRegistry> {
	if (!forceRefresh && cachedRegistry) {
		return cachedRegistry;
	}

	if (!forceRefresh && pendingRegistryPromise) {
		return pendingRegistryPromise;
	}

	pendingRegistryPromise = http
		.get("/utility/public-admin-text-registry")
		.then(({ data }) => normalizeAdminTextRegistry(data?.data || EMPTY_REGISTRY))
		.then((registry) => {
			cachedRegistry = registry;
			return registry;
		})
		.catch((error) => {
			if (cachedRegistry) {
				return cachedRegistry;
			}
			throw error;
		})
		.finally(() => {
			pendingRegistryPromise = null;
		});

	return pendingRegistryPromise;
}

export function clearPublishedAdminTextRegistryCache() {
	cachedRegistry = null;
	pendingRegistryPromise = null;
}

export function findAdminTextEntry(
	registry: AdminTextRegistry | null | undefined,
	key: string,
	fallback: string,
): AdminTextEntry {
	const item = registry?.items?.find((entry) => entry.key === key);
	if (item) {
		return item;
	}

	const fallbackMatch = String(fallback || "").trim();
	if (fallbackMatch) {
		const entryByDefaultText = registry?.items?.find(
			(entry) => String(entry.defaultText || "").trim() === fallbackMatch,
		);
		if (entryByDefaultText) {
			return entryByDefaultText;
		}
	}

	return {
		key,
		screen: "general",
		group: "general",
		defaultText: fallback,
		currentText: "",
		stylePreset: DEFAULT_STYLE_PRESET,
		colorToken: "default",
		status: "published",
		updatedAt: null,
	};
}

export function hasAdminRegistryKey(registry: AdminTextRegistry | null | undefined, key: string): boolean {
	return Boolean(registry?.items?.some((entry) => entry.key === key));
}

export function formatAdminTextValue(
	value: string,
	variables?: Record<string, string | number | null | undefined>,
): string {
	const template = String(value || "");
	if (!variables) {
		return template;
	}

	return template.replace(/\{(\w+)\}/g, (_, token) => {
		const nextValue = variables[token];
		return nextValue === null || nextValue === undefined ? "" : String(nextValue);
	});
}

export function getAdminTextValue(
	entry: AdminTextEntry,
	variables?: Record<string, string | number | null | undefined>,
): string {
	return formatAdminTextValue(String(entry.currentText || entry.defaultText || ""), variables);
}

export function buildAdminTextStyle(
	entry: AdminTextEntry,
	palette: AdminTextPaletteEntry[] = DEFAULT_PALETTE,
): CSSProperties {
	const paletteMap = new Map(palette.map((item) => [item.token, item.value]));
	const fontSizeMap: Record<AdminTextFontSizePreset, string> = {
		sm: "0.875rem",
		md: "1rem",
		lg: "1.125rem",
	};
	const stylePreset = normalizeStylePreset(entry.stylePreset);

	return {
		fontSize: fontSizeMap[stylePreset.fontSizePreset] || fontSizeMap.md,
		fontWeight: stylePreset.fontWeight,
		fontStyle: stylePreset.fontStyle,
		color: paletteMap.get(entry.colorToken) || paletteMap.get("default") || "#0f172a",
		textDecoration: "none",
	};
}

const detectionQueue = new Map<
	string,
	{ file: string; text: string; suggestedScreen: string; suggestedGroup: string }
>();
let flushTimer: ReturnType<typeof setTimeout> | null = null;

function envEnabled(): boolean {
	try {
		const raw =
			typeof process !== "undefined" && process.env ? process.env.REACT_APP_ADMIN_TEXT_AUTODETECT : "";
		const normalized = String(raw || "").trim().toLowerCase();
		return normalized !== "off";
	} catch {
		return true;
	}
}

export function queueAdminRuntimeDetection(payload: {
	key: string;
	fallback: string;
	group?: string;
}) {
	if (!envEnabled() || typeof window === "undefined") {
		return;
	}

	const text = String(payload.fallback || "").trim();
	if (!text || text.length < 2) {
		return;
	}

	const fingerprint = `${payload.key}::${text}`;

	detectionQueue.set(fingerprint, {
		file: "runtime://admin",
		text,
		suggestedScreen: payload.key.split(".")[0] || "general",
		suggestedGroup: payload.group || "runtime",
	});

	if (flushTimer) {
		clearTimeout(flushTimer);
	}
	flushTimer = setTimeout(() => {
		flushTimer = null;
		void flushAdminRuntimeDetections();
	}, 5000);
}

async function flushAdminRuntimeDetections() {
	const items = Array.from(detectionQueue.values());
	detectionQueue.clear();
	if (items.length === 0) {
		return;
	}

	try {
		await http.post("/utility/admin-text-detections", { data: items });
	} catch {
		/* avoid breaking UI if sync fails */
	}
}
